/* eslint-disable no-param-reassign */
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OrderOwner } from '../types/OrderOwner';
import { OrderStatus } from '../types/OrderStatus';
import { Dealer } from '../types/Dealer';
import { LeadOrdersState } from '../types/LeadOrdersState';
import { LeadOrOrder } from '../types/LeadOrOrder';
import { OrderDealer } from '../types/OrderDealer';

const InitialLeadOrderState: LeadOrdersState = {
  leads: [],
  configurator: undefined,
  sortProperties: [],
  vendor: undefined,
  date: {
    filter: undefined,
    startDate: undefined,
    endDate: undefined,
  },
  orderOwnerOptions: [],
};

export const leadOrderSlice = createSlice({
  name: 'order',
  initialState: InitialLeadOrderState,
  reducers: {
    setLeads(state, action: PayloadAction<LeadOrOrder[]>) {
      state.leads = action.payload;
    },
    fetchOrderOwnerOptionsSuccess(state, action: PayloadAction<{ orderOwnerOptions: OrderOwner[] }>) {
      state.orderOwnerOptions = action.payload.orderOwnerOptions;
    },
    fetchOrderOwnerOptionsError(state) {
      state.orderOwnerOptions = [];
    },
    setOrderDealerSuccess(state, action: PayloadAction<{ clientId: string; uuid: string; orderDealer: OrderDealer }>) {
      const { clientId, uuid, orderDealer } = action.payload;
      const lead = state.leads?.find((o) => o.clientId === clientId && o.uuid === uuid);
      if (lead) {
        lead.orderDealerId = orderDealer?.id || -1;
        lead.orderDealerKey = orderDealer?.key || '';
        lead.orderDealerName = orderDealer?.name || '';
      }
    },
    setOrderOwnerSuccess(state, action: PayloadAction<{ clientId: string; uuid: string; orderOwner: OrderOwner }>) {
      const { clientId, uuid, orderOwner } = action.payload;
      const lead = state.leads?.find((o) => o.clientId === clientId && o.uuid === uuid);
      if (lead) {
        lead.ownerName = orderOwner?.name || '';
        lead.ownerEmail = orderOwner?.email || '';
      }
    },
    setOrderStatusSuccess(state, action: PayloadAction<{ clientId: string; uuid: string; orderStatus: OrderStatus }>) {
      const { clientId, uuid, orderStatus } = action.payload;
      state.leads = state.leads.map((lead) =>
        lead.clientId === clientId && lead.uuid === uuid
          ? {
              ...lead,
              orderStatusId: orderStatus?.id || '',
              orderStatusI18nKey: orderStatus?.i18nKey || '',
              orderStatusName: orderStatus?.name || '',
              orderStatusFontColor: orderStatus?.chip?.fontColor || '',
              orderStatusBackgroundColor: orderStatus?.chip?.backgroundColor || '',
            }
          : lead,
      );
    },
    deleteLeadSuccess(state, action: PayloadAction<{ uuid: string }>) {
      state.leads = state.leads.filter((lead) => lead.uuid !== action.payload.uuid);
    },
  },
});

export const setOrderOwner = createAction<{
  clientId: string;
  uuid: string;
  orderStatus: OrderStatus | string;
  orderOwner: OrderOwner;
}>('order/setOrderOwner');

export const setOrderDealer = createAction<{
  clientId: string;
  uuid: string;
  orderStatus: OrderStatus | string;
  orderDealer: Dealer;
}>('order/setOrderDealer');

export const setOrderStatus = createAction<{ clientId: string; uuid: string; orderStatus: OrderStatus }>(
  'order/setOrderStatus',
);

export const deleteLead = createAction<{ uuid: string }>('order/deleteLead');

export const openLeadDetailDialog = createAction<{ leads: any[]; uuid: string }>('order/openLeadDetailDialog');

export const {
  setLeads,
  fetchOrderOwnerOptionsSuccess,
  fetchOrderOwnerOptionsError,
  setOrderDealerSuccess,
  setOrderOwnerSuccess,
  setOrderStatusSuccess,
  deleteLeadSuccess,
} = leadOrderSlice.actions;
